import React, { useState, useCallback, useEffect, useRef } from 'react';
import withField from '@core/js/components/common/Fields/withField';
import ValidationIcon from '@core/js/components/common/Fields/Icons/ValidationIcon';
import VisibilityIcon from '@core/js/components/common/Fields/Icons/VisibilityIcon';
import { InputShallow } from '@core/js/components/common/Fields/Input/components/InputShallow';
import style from './SwissInputForm.scss';
import { useAppSelector } from '@core/js/store/hooks';
import { prop } from 'ramda';
import { useTranslations } from '@core/js/hooks/i18n';
import buildClassName from '@core/js/utils/classNameBuilder.util';
import { PasswordHints } from '../../PasswordHints/PasswordHints';
import { ErrorMessage } from '@core/js/components/common/ErrorMessage';
import { log } from 'log';
import { Label } from '@core/js/components/common/FormFields';
import uuid from 'uuid-random';
import * as helpers from './helpers';

export const SwissInputFormBare = ({
       /* rff render props */
       input,
       meta,
       /* user provided props */
       name,
       label,
       bottomHint,
       hint,
       type = 'text',
       classNames,
       onClick,
       showPasswordEye = true,
       noValidate,
       errorMessage,
       notEmpty,
       errorAppeared,
       id,
       readOnly,
       infoTooltip,
       currencySymbol,
       onMouseDown,
       placeholder = '',
       isLabelAsteriskEnabled,
       isPlaceholderAsteriskEnabled,
       isPasswordTooltipField,
       fieldsValidation,
       setFieldsValidation,
       initialValues,
       isUpdateMyDetails,
       escapeSymbolsRegExp,
       swissErrors,
       onInput,
       setSwissErrors,
       requiredMessages,
       ...inputDomProps
}) => {
    const { touched, error, submitting, valid, active, validating } = meta;
    const idRef = useRef(id || uuid());
    const isEnablePasswordSpy = useAppSelector(prop('isEnablePasswordSpy'));
    const translate = useTranslations('playtech.mobile.system.input.');
    const [isVisible, setIsVisible] = useState(false);
    const [isFocused, setIsFocused] = useState(false);

    const showPasswordIcon = (showPasswordEye || isEnablePasswordSpy) && type === 'password';
    const controlClassName = buildClassName(['control'], style, classNames);
    const modeClassName = helpers.getClass(isFocused, valid, submitting, readOnly, active, touched);
    const isValidState = modeClassName === 'valid' && isUpdateMyDetails && initialValues[input.name] !== input.value ||
        modeClassName === 'valid' && !isUpdateMyDetails;
    const showSwissError = swissErrors.find(el => el.name === name ||
        name.includes('confirmation') && el.name === 'requiresConfirmation')?.showError;

    const leaveFocusState = () => {
        setIsFocused(!isFocused);
        updateFieldsValidationWithErrors();
    };

    const toggleVisible = useCallback(() => setIsVisible(!isVisible), [isVisible]);
    const preventClick = useCallback(event => event.preventDefault(), []);
    const applyFocusState = useCallback(event => event.target.offsetParent.querySelector('input').focus(), []);

    const updateFieldsValidationWithErrors = () => {
        const updatedFieldsValidation = helpers.updateErrorMessagesWithValues(fieldsValidation, input, error);
        setFieldsValidation(updatedFieldsValidation);
        const notRequiredErrors = helpers.updateShowingSwissErrors(updatedFieldsValidation, requiredMessages);
        setSwissErrors(notRequiredErrors);
    };

    const onInputFocus = useCallback((event) => {
        setIsFocused(!isFocused);
        applyFocusState(event);
    }, [applyFocusState, isFocused]);

    const onCombinedInput = useCallback((event) => {
        if (escapeSymbolsRegExp) {
            try {
                event.target.value = event.target.value.replace(new RegExp(escapeSymbolsRegExp), '');
            } catch (err) {
                log('error', err);
            }
        }

        onInput?.(event);
    }, [escapeSymbolsRegExp, onInput]);

    const onInputChange = useCallback((event) => {
        if (isUpdateMyDetails) {
            const { value } = event.target;
            const updatedFieldsValidation = helpers.updateValues(fieldsValidation, input, value);
            setFieldsValidation(updatedFieldsValidation);
        }
    }, [fieldsValidation, input, isUpdateMyDetails, setFieldsValidation]);

    const onBlur = useCallback(() => leaveFocusState(), [
        isFocused, error, input.value, input.name, swissErrors
    ]);

    const onKeyDownPasswordIcon = useCallback((event) => {
        if (event.key === 'Tab' && idRef.current) {
            leaveFocusState();
        }
    }, [isFocused, error, input.value, input.name, swissErrors]);

    useEffect(() => {
        !isFocused && updateFieldsValidationWithErrors();
    }, [error, input.value, isFocused]);

    return (
        <>
            <div
                onClick={onClick}
                className={`${controlClassName}
                ${style.controlWrapper} control-${modeClassName}
                ${isValidState ? style.valid : ''}
                ${isFocused ? style.active : style.notActive}`}>
                <div className={`${style.controlContent} cms-control-content`}>
                    <div className={style.fieldContainer} onBlur={onBlur}>
                        <InputShallow
                            {...input}
                            {...inputDomProps}
                            name={name}
                            type={isVisible ? 'text' : type}
                            {...submitting ? { disabled: true } : {}}
                            className={style.inputField}
                            onFocus={onInputFocus}
                            id={idRef.current}
                            onKeyUp={onInputChange}
                            readOnly={readOnly}
                            {...(submitting || validating) && { disabled: true }}
                            tabIndex={readOnly ? -1 : 0}
                            aria-label={inputDomProps['aria-label'] || translate('playtech.mobile.system.input.aria-label')}
                            onMouseDown={readOnly ? preventClick : onMouseDown}
                            placeholder={`${placeholder}${isPlaceholderAsteriskEnabled ? '*' : ''}`}
                            {...escapeSymbolsRegExp ? { onInput: onCombinedInput } : { onInput }}
                        />
                        {(label || isLabelAsteriskEnabled) && (
                            <Label
                                label={label}
                                id={idRef.current}
                                readOnly={readOnly}
                                className={`${style.label} 
                                ${(input.value || isFocused) && style.labelAbove}
                                ${!isFocused && !valid && style.labelInvalid}`}
                                infoTooltip={infoTooltip}
                                isLabelAsteriskEnabled={isLabelAsteriskEnabled}
                            />
                        )}
                        {showPasswordIcon && (
                            <VisibilityIcon
                                className={`${style.passwordIcon} 
                                ${modeClassName === 'valid' || modeClassName === 'invalid' && showSwissError ?
                                    style.rightPosition : style.notRightPosition}`}
                                onClick={toggleVisible}
                                onMouseDown={preventClick}
                                onKeyDown={onKeyDownPasswordIcon}
                                isVisible={isVisible}
                                aria-label={isVisible
                                    ? translate('password.aria-label.hide')
                                    : translate('password.aria-label.show')
                                }
                            />
                        )}

                        {isValidState && (
                            <ValidationIcon
                                className={`${classNames?.validationIcon} ${style.icon} `}
                                isValid={true}
                                animate={true}
                                onClick={applyFocusState}
                                onMouseDown={preventClick}
                            />
                        )}

                        {showSwissError && error && !isFocused && (
                            <ValidationIcon
                                className={`${classNames?.validationIcon} ${style.icon} ${style.invalidIcon}`}
                                isValid={errorAppeared ? false : notEmpty}
                                animate={true}
                                onClick={applyFocusState}
                                onMouseDown={preventClick}
                                aria-label={error}
                                tabIndex={error ? 0 : -1}
                            />
                        )}
                    </div>
                </div>

                {bottomHint && (
                    <span className={`${buildClassName(['bottomHint'], style, classNames)}`}>
                        {bottomHint}
                    </span>
                )}

                {showSwissError && error && !isFocused && (
                    <>
                        {input.name === 'newPasswordTooltip' ?
                            (<span className={style.errorLine}/>) :
                            (<ErrorMessage
                                error={error}
                                classNames={classNames}
                                className={style.errorMessage}
                            />)
                        }
                    </>
                )}
            </div>
            {isPasswordTooltipField && input.name === 'newPasswordTooltip' &&
                <PasswordHints isFocused={isFocused} input={input}/>}
        </>

    );
};

export const SwissInputForm = withField(SwissInputFormBare);
